import { fetcher, noRevalidateOnlyStaleOptions } from 'src/utils/axios';
import { ENDPOINTS } from './endpoints';
import { useMemo } from 'react';
import useSWR from 'swr';
import { getURL } from 'src/utils/url';

export function useGetCommonCodeType(params) {
  const URL = getURL(ENDPOINTS.common.get, params);
  const { data, isLoading, error, isValidating } = useSWR(
    URL,
    fetcher,
    noRevalidateOnlyStaleOptions,
  );
  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result || [],
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );

  return memoizedValue;
}

export function useGetCountryCode(params) {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'country_code',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'country_code')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetDimensionUnit() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'dimension_unit',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'dimension_unit')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetWeightUnit() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'weight_unit',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'weight_unit')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetLocationType() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'location_type',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'location_type')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetMachineType() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'machine_type',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'machine_type')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetPackingMaterialType() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'packing_material_type',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'packing_material_type')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetRackType() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'rack_type',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'rack_type')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetTemperatureType() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'temperature_type',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'temperature_type')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetTimezone() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'timezone',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'timezone')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetLanguageCode() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'language_code',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'language_code')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetOrderStatus() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'order_status',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'order_status')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetOrderShipmentStatus() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'order_shipment_status',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'order_shipment_status')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetShipmentPickingStatus() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'shipment_picking_status',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'shipment_picking_status')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetBulkPickingStatus() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'bulk_picking_status',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'bulk_picking_status')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetBulkShipmentStatus() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'bulk_shipment_status',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'bulk_shipment_status')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetStockTypeCode() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'stock_type',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'stock_type')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}

export function useGetPurchaseOrderStatus() {
  const { searchResults, searchLoading } = useGetCommonCodeType({
    CodeType: 'purchase_order_status',
    IsActive: true,
    DetailIsActive: true,
  });
  const details = useMemo(
    () => searchResults?.find((c) => c.codeType === 'purchase_order_status')?.details || [],
    [searchResults],
  );
  return { searchResults: details, searchLoading };
}
